import React from "react";
import { createPortal } from "react-dom";
import {
  X, Download, Upload, Loader2, Database, ShieldCheck,
  FileArchive, ScanEye,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import useEscClose from "../hooks/useEscClose";
import useBackupOperations from "../hooks/useBackupOperations";
import BackupResultBanners from "./backup/BackupResultBanners";
import RestorePreviewCard from "./backup/RestorePreviewCard";

/**
 * Admin tool — download or restore the full-DB backup.
 *
 * Three download flavours are exposed:
 *   1. Full-DB JSON (left card) — every non-GridFS collection,
 *      dynamically enumerated. Small enough (~30 MB) to email.
 *   2. Full-DB + PDFs (middle card) — walks the bucket shard
 *      manifest and pulls the JSON + each GridFS bucket ZIP in
 *      sequence, so the operator ends up with a directory of files
 *      that together form a complete disaster-recovery snapshot,
 *      binaries included.
 *   3. Preview-first restore (right card) — accepts the JSON or ZIP.
 *
 * Restore is a two-step preview → confirm flow:
 *   • Pick file → hits `/admin/full-restore-preview` (no writes) →
 *     the modal shows a per-collection diff (net-new / updated / skipped
 *     / seed) plus an orphans list of collections in the DB that aren't
 *     in the backup.
 *   • Apply → downloads a safety backup, then commits the restore.
 *
 * The historical "Biometrics-only JSON" cards were removed on 2026-02-24
 * — the full-DB backup is a strict superset and has preview + ZIP support
 * the legacy flow never gained.
 */
export default function BackupRestoreModal({ open, onClose }) {
  const { isAdmin } = useAuth();
  const {
    busy, busyFlow, error, result, pendingPreview,
    fullRestoreInputRef,
    handleFullBackup, handleFullBackupWithPdfs,
    handlePreviewRestore, handleApplyRestore, clearPendingRestore,
  } = useBackupOperations();

  useEscClose(open, onClose);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4"
      data-testid="backup-restore-modal"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose?.();
      }}
    >
      <div className="card w-full max-w-3xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-cyan-300" />
            <div>
              <div className="font-display text-lg text-slate-100">
                Backup &amp; restore
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Move data between environments (e.g. preview → production).
              </div>
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="backup-close-btn">
            <X size={14} />
          </button>
        </div>

        <BackupResultBanners error={error} result={result} />

        {/* Full DB Backup & Restore */}
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-widest text-amber-300/80 mb-2 flex items-center gap-2">
            <ShieldCheck size={12} /> Full database — everything, everywhere
            <span className="text-slate-500 normal-case tracking-normal text-[10px]">
              dynamically enumerated — every collection in Mongo is included
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            {/* JSON-only backup */}
            <div className="card !bg-[#0B1627] p-4">
              <div className="flex items-center gap-2 text-cyan-300 text-sm font-display">
                <Database size={14} /> JSON backup
              </div>
              <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                One JSON file (~30 MB) named
                <span className="font-mono-data"> YCH-Health-Backup-&lt;stamp&gt;.json</span>.
                Every collection except PDFs — small enough to email.
              </div>
              <button
                onClick={() => handleFullBackup()}
                disabled={busy || !isAdmin}
                title={isAdmin ? undefined : "Only admins can download a full backup"}
                className="pill active w-full justify-center mt-3 disabled:opacity-40"
                data-testid="full-backup-btn"
              >
                {busyFlow === "json" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                {isAdmin ? "JSON only" : "Admin only"}
              </button>
            </div>

            {/* JSON + PDFs (new) */}
            <div className="card !bg-[#0B1627] p-4">
              <div className="flex items-center gap-2 text-cyan-300 text-sm font-display">
                <FileArchive size={14} /> JSON + PDFs
              </div>
              <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                Downloads a set of timestamped ZIPs: the metadata plus each
                GridFS bucket sharded into pages so nothing exceeds the
                60-second ingress cap. Full disaster-recovery snapshot.
              </div>
              <button
                onClick={handleFullBackupWithPdfs}
                disabled={busy || !isAdmin}
                title={isAdmin ? undefined : "Only admins can download a full backup"}
                className="pill active w-full justify-center mt-3 disabled:opacity-40"
                data-testid="full-backup-with-pdfs-btn"
              >
                {busyFlow === "pdfs" ? <Loader2 size={13} className="animate-spin" /> : <FileArchive size={13} />}
                {isAdmin ? "JSON + PDF archive" : "Admin only"}
              </button>
            </div>

            {/* Restore (preview-first) */}
            <div className="card !bg-[#0B1627] p-4 relative">
              <div className="flex items-center gap-2 text-amber-300 text-sm font-display">
                <ScanEye size={14} /> Restore (preview first)
              </div>
              <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                Upload a <span className="font-mono-data">.json</span> backup <b>or</b> a
                <span className="font-mono-data"> .zip</span> archive (JSON + PDFs). Nothing
                is written until you approve the diff. Existing records get updated if the
                backup is newer; new ones added; deletions never happen. GridFS restore
                preserves the original PDF ObjectIds so record references still resolve.
                <span className="text-amber-300/90"> Admin only.</span>
              </div>
              <input
                ref={fullRestoreInputRef}
                type="file"
                accept=".json,application/json,.zip,application/zip"
                onChange={handlePreviewRestore}
                className="hidden"
                data-testid="full-restore-file-input"
              />
              <button
                onClick={() => fullRestoreInputRef.current?.click()}
                disabled={busy || !isAdmin}
                className="pill active w-full justify-center mt-3 disabled:opacity-40"
                data-testid="full-restore-btn"
                style={{ background: "rgba(251,191,36,0.15)", borderColor: "#F59E0B", color: "#FCD34D" }}
                title={isAdmin ? "Upload a YCH-Health-Backup-*.json or *.zip file" : "Only admins can run a full restore"}
              >
                {busyFlow === "restore" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {isAdmin ? "Preview restore…" : "Admin only"}
              </button>
            </div>
          </div>

          {/* Restore preview card — only rendered after a successful dry-run */}
          {pendingPreview && (
            <RestorePreviewCard
              preview={pendingPreview}
              busy={busy}
              onApply={handleApplyRestore}
              onCancel={clearPendingRestore}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
